import { useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import AppLayout from '@/layouts/AppLayout';
import LoginPage from '@/pages/auth/LoginPage';
import ForgotPasswordPage from '@/pages/auth/ForgotPasswordPage';
import ResetPasswordPage from '@/pages/auth/ResetPasswordPage';
import InviteAcceptPage from '@/pages/auth/InviteAcceptPage';
import IpChangePage from '@/pages/auth/IpChangePage';
import DashboardPage from '@/pages/dashboard/DashboardPage';
import {
  DetectionsPage, MarksPage, CasesPage, UsersPage,
  NotificationsPage, ReportsPage, ChangelogPage, AuditPage,
  ConfigGlobalPage, SettingsPage
} from '@/pages/stubs';
import { DrillPage } from '@/pages/drill/DrillPage';
import CatalogPage from '@/pages/catalog/CatalogPage';
import LivePage from '@/pages/live/LivePage';
import HardwarePage from '@/pages/hardware/HardwarePage';
import EmulatorsPage from '@/pages/emulators/EmulatorsPage';
import { PrivateRoute } from '@/routes/PrivateRoute';
import { Toaster } from '@/components/ui/toaster';
import { ErrorBoundary } from '@/components/common/ErrorBoundary';
import { useThemeStore } from '@/store/theme';
import { useAuthStore } from '@/store/auth';
import { authApi } from '@/api/endpoints';
import { themesApi } from '@/api/endpoints';

export default function App() {
  const hydrateTheme = useThemeStore(s => s.hydrate);
  const { isAuthenticated, setUser, accessToken } = useAuthStore();
  const { i18n } = useTranslation();

  // Hidrata tema no mount E sempre que o accessToken mudar (login/logout) —
  // o GET /themes agora eh anonimo entao funciona em qualquer estado, mas
  // ao logar puxamos as preferences do user (idioma + tema preferido).
  useEffect(() => {
    hydrateTheme();
  }, [accessToken]);

  useEffect(() => {
    if (isAuthenticated()) {
      authApi.me().then(setUser).catch(() => { /* token invalido — interceptor cuida */ });
      themesApi.myPreferences().then((pref: any) => {
        if (pref?.language && pref.language !== i18n.resolvedLanguage) i18n.changeLanguage(pref.language);
      }).catch(() => { /* ignore */ });
    }
    // eslint-disable-next-line
  }, [accessToken]);

  return (
    <ErrorBoundary>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/auth/reset-password" element={<ResetPasswordPage />} />
        <Route path="/invite/accept" element={<InviteAcceptPage />} />
        <Route path="/ip-change/:token" element={<IpChangePage />} />
        <Route element={<PrivateRoute><AppLayout /></PrivateRoute>}>
          <Route index element={<DashboardPage />} />
          <Route path="/live" element={<LivePage />} />
          <Route path="/detections" element={<DetectionsPage />} />
          <Route path="/drill" element={<DrillPage />} />
          <Route path="/hardware" element={<HardwarePage />} />
          <Route path="/emulators" element={<EmulatorsPage />} />
          <Route path="/catalog" element={<CatalogPage />} />
          <Route path="/marks" element={<MarksPage />} />
          <Route path="/cases" element={<CasesPage />} />
          <Route path="/notifications" element={<NotificationsPage />} />
          <Route path="/reports" element={<ReportsPage />} />
          <Route path="/changelog" element={<ChangelogPage />} />
          <Route path="/settings" element={<SettingsPage />} />
          <Route path="/audit" element={<PrivateRoute adminOnly><AuditPage /></PrivateRoute>} />
          <Route path="/users"  element={<PrivateRoute adminOnly><UsersPage /></PrivateRoute>} />
          <Route path="/config" element={<PrivateRoute adminOnly><ConfigGlobalPage /></PrivateRoute>} />
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      <Toaster />
    </ErrorBoundary>
  );
}
